import { useEffect, useState } from "react";
import CustomTableWithPagination from "../../../../reusable-components/CustomTable";
import JobDatabaseService from "../../../../services/jobDatabase.service";
import {
  DatabaseCommonTableProps,
  DispatchInformation,
} from "../../../../Types/DatabaseTabTypes";
import {
  BlockUnblockOrderConstants,
  copyObjectAsRich,
  DispatchInfoModalWidth,
  getColumns,
  getDispatchInfoForCopy,
  MailMessages,
} from "./constants";
import styles from "./OrdersTable.module.scss";
import { message, Modal, Switch } from "antd";
import {
  getPrevDollorFormattedString,
  objectToString,
} from "../../../../Utilities";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../../enums/ButtonAttributesEnums";
import copyIcon from "../../../../assets/images/copy-icon.svg";
import { useHistory, useLocation } from "react-router-dom";
import { ViewListingTypes } from "../../../../enums/ViewListingTypes";
import { useWrapperContext } from "../../../../context/WrapperContext";
import { useMaterials } from "../../../../context/materialListingContext";
import { useTruckDetailContext } from "../../../../context/TruckDetailsContext";
import CreateJobServices from "../../../../services/createJob.services";
import RolesServices from "../../../../services/roles.services";
import { UserRoleActions } from "../../../../enums/UserRolesEnum";
import { appRoutes } from "../../../../constants/appRoutes";
import TNLoader from "../../../../reusable-components/TNLoader";
import { jobStatus } from "../../../CreateJob/CreateJobView/constants";

const OrdersTable = (props?: DatabaseCommonTableProps) => {
  const { section, sectionId } = props;
  const navigate = useHistory();
  const location = useLocation();
  const {
    BLOCK_ORDER_TITLE, UNBLOCK_ORDER_TITLE, BLOCK_ORDER_MESSAGE, UNBLOCK_ORDER_MESSAGE,
   } = BlockUnblockOrderConstants;
  const { setPrevRecord } = useWrapperContext();
  const { resetTruckMaterial } = useMaterials();
  const { resetTruckDetails } = useTruckDetailContext();
  const { FetchOrderDetails, BlockUnblockJobOrder, SendViaEmail } = JobDatabaseService();
  const { FetchSelectedJob } = CreateJobServices();
  const { data: jobData, refetch: refetchJobDetails, isRefetching: refetchingJobDetails } = FetchSelectedJob(
    sectionId,
    true,
  );
  const { mutateAsync: sentViaEmail, isPending } = SendViaEmail();
  const { mutateAsync: blockUnblockOrder, isPending: blockUnblockPending } = BlockUnblockJobOrder();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [isBlockUnblockConfirmOpen, setIsBlockUnblockConfirmOpen] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | undefined>(undefined);
  const [blockOrUnblockAction, setBlockUnblockAction] = useState<boolean>(false);
  const [dispatchInformation, setDispatchInformation] =
    useState<DispatchInformation>({});
  const handleDispatchModalOpen = () => setIsDispatchModalOpen(true);
  const handleDispatchModalClose = () => {
    setIsDispatchModalOpen(false);
    setDispatchInformation({});
  };
  const handleBlockUnblockConfirmOpen = () => setIsBlockUnblockConfirmOpen(true);
  const handleBlockUnblockConfirmClose = () => {
    setIsBlockUnblockConfirmOpen(false);
    setSelectedOrderId(undefined);
    setBlockUnblockAction(false);
  };
  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };
  const handleDispatchModal = (data?: DispatchInformation) => {
    setDispatchInformation(data);
    handleDispatchModalOpen();
  };
  const handleBlockUnblockModal = (orderId: string, blockAction: boolean) => {
    if(blockAction)
      setBlockUnblockAction(true);
    setSelectedOrderId(orderId);
    handleBlockUnblockConfirmOpen();
  };

  const userDetails = localStorage.getItem("userDetails");
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.["userRoleInfo"];
  const userRoleProfileInfo = userDetailsJson?.["userProfile"];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const noActionDropFields = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[1]?.multiPannel?.[0]?.selectedOptions?.includes(
        UserRoleActions.EDIT,
      )
    : true;

  const handleEditNavigate = () => {
    setPrevRecord(jobData);
    resetTruckDetails();
    resetTruckMaterial();
    navigate.push(
      `/app/update-trucking-material/${sectionId}#${ViewListingTypes.LIST}`,
      {
        edit: true,
        step: 2,
        from: location,
        source: appRoutes.JOB_DATABASE,
      },
    );
  };

  const handleCopyDispatch = () => {
    const copyObject = getDispatchInfoForCopy(dispatchInformation);
    try {
      navigator.clipboard.writeText(copyObjectAsRich(copyObject));
      message.success(MailMessages.DISPATCH_COPIED);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const handleSendEmail = async () => {
    await sentViaEmail(
      {
        id: sectionId,
        orderId: dispatchInformation?.id,
      },
      {
        onSuccess: () => message.success(MailMessages.SUCCESS),
        onError: () => message.error(MailMessages.FAILURE),
      },
    );
  };

  const handleBlockUnblockOrderAction = async () => {
    await blockUnblockOrder({
      jobId: sectionId,
      orderId: selectedOrderId,
      isBlocked: blockOrUnblockAction,
    }, {
      onSuccess: async () => {
        await refetchJobDetails();
        refetch();
      },
    });
    handleBlockUnblockConfirmClose();
  };

  const columns = getColumns({
    handleDispatch: handleDispatchModal,
    handleEdit: handleEditNavigate,
    noActionDropFields: noActionDropFields,
    handleBlockUnblockOrder: handleBlockUnblockModal,
    blockedJob: !!jobData?.isBlocked,
  });
  const { data, refetch, isFetching } = FetchOrderDetails({
    section: section,
    section_id: sectionId,
    page: currentPage,
    perPage: pageSize,
  });

  useEffect(() => {
    refetchJobDetails();
  }, [sectionId]);

  const modalHeader = (
    <div className={styles.ModalHeader}>
      <div>Dispatch Information</div>
      <TNButton
        text={
          <>
            <img src={copyIcon} />
            Copy
          </>
        }
        type={ButtonType.DEFAULT}
        shape={ButtonShape.DEFAULT}
        size={ButtonSize.LARGE}
        onClick={handleCopyDispatch}
      />
      <TNButton
        text={"Send Via Email"}
        type={ButtonType.DEFAULT}
        shape={ButtonShape.DEFAULT}
        size={ButtonSize.LARGE}
        onClick={handleSendEmail}
      />
    </div>
  );

  return (
    <>
      <TNLoader
       isLoading={
          blockUnblockPending
          || refetchingJobDetails
        }
      />
      <Modal
        visible={isDispatchModalOpen}
        onCancel={handleDispatchModalClose}
        width={DispatchInfoModalWidth}
        footer={null}
        title={modalHeader}
      >
        {
          <div className={styles.DispatchModal}>
            <div className={styles.DispatchLabel}>Date</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.date}
            </div>
            <div className={styles.DispatchLabel}>Customer Name</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.companyName}
            </div>
            <div className={styles.DispatchLabel}>Job Name</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.jobName}
            </div>
            <div className={styles.DispatchLabel}>Primary contact</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.contactName}
            </div>
            <div className={styles.DispatchLabel}>Primary contact number</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.phone}
            </div>
            <div className={styles.DispatchLabel}>Job type</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.jobType}
            </div>
            <div className={styles.DispatchLabel}>Equipment</div>
            <div className={styles.DispatchValue}>
              {dispatchInformation?.truckType?.join(", ") ?? ""}
            </div>
            <div className={styles.DispatchLabel}>Pickup Location</div>
            <div className={styles.DispatchValue}>
              {`${dispatchInformation?.pickUpAddress}, (${dispatchInformation?.pickUpLatLng?.lat}, ${dispatchInformation?.pickUpLatLng?.lng})`}
            </div>
            <div className={styles.DispatchLabel}>Delivery Location</div>
            <div className={styles.DispatchValue}>
              {`${dispatchInformation?.dropoff}, (${dispatchInformation?.dropOffLatLng?.lat}, ${dispatchInformation?.dropOffLatLng?.lng})`}
            </div>
            <div className={styles.DispatchLabel}>Rate Information</div>
            <div className={styles.DispatchValue}>
              {`${getPrevDollorFormattedString(dispatchInformation?.truckingPrice)} per ${dispatchInformation?.truckingPriceType} ${dispatchInformation?.minimumHours ? `, ${dispatchInformation?.minimumHours} - Hour minimum` : ""}`}
            </div>
          </div>
        }
      </Modal>
      <Modal
        visible={isBlockUnblockConfirmOpen}
        onCancel={handleBlockUnblockConfirmClose}
        onOk={handleBlockUnblockOrderAction}
        okText={CommonButtonTexts.CONFIRM}
        centered
        title={
         blockOrUnblockAction
          ? BLOCK_ORDER_TITLE
          : UNBLOCK_ORDER_TITLE
        }
      >
        {blockOrUnblockAction
          ? BLOCK_ORDER_MESSAGE
          : UNBLOCK_ORDER_MESSAGE
        }
      </Modal>
      <CustomTableWithPagination
        columns={columns}
        className={styles.Table}
        data={data?.data}
        pageSize={pageSize}
        currentPage={currentPage}
        rowKey={(record) => record?.id}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        loading={isFetching}
        totalData={data?.meta?.totalRecords}
      />
      <TNButton
        text={"+ Add Material/Trucking"}
        type={ButtonType.PRIMARY}
        size={ButtonSize.LARGE}
        shape={ButtonShape.DEFAULT}
        className={styles.addButton}
        onClick={handleEditNavigate}
        disabled={
          !noActionDropFields
          || !!jobData?.isBlocked
          || jobData?.stageId === jobStatus.jobLead
        }
      />
    </>
  );
};
export default OrdersTable;
